import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, withCtx as _withCtx, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "wait-overflow"
}
const _hoisted_2 = {
  key: 0,
  class: "stripe-payment-element__express-label-20504"
}
const _hoisted_3 = {
  key: 0,
  class: "stripe-payment-element__card-divider-20504"
}
const _hoisted_4 = { class: "stripe-payment-element__card-box" }
const _hoisted_5 = ["disabled"]
const _hoisted_6 = { class: "alert alert-success" }

import {
  computed,
  onBeforeMount,
  watch,
  onBeforeUnmount,
  onErrorCaptured,
  ref,
} from 'vue'

// The vue-stripe-js package doesn't have typing
// @ts-ignore
import { StripeElements, StripeElement } from 'vue-stripe-js'
import type {
  StripeElementsOptionsMode,
  StripePaymentElementOptions,
  StripeExpressCheckoutElementOptions,
} from '@stripe/stripe-js'

import { STRIPE_PUBLIC_KEY } from '@/init/settings'
import { ValidationState } from '@/helpers/form'
import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import type { BillingView } from '@/models/billing'
import FormError from '@/components/ui/FormError.vue'

import { getEmail } from '@/services/auth'
import { useWait } from '@/app/composables/useWait'
import { emitter } from '@/services/mitt'
import { nightMode } from '@/services/night'
import { checkoutModalSimplification20504Variant } from '@/services/ab'


export default /*@__PURE__*/_defineComponent({
  __name: 'StripeCard',
  props: {
    billing: {},
    showButton: { type: Boolean, default: true }
  },
  emits: [
  'saved' as string,
  'elementsReady' as string,
  'loadError' as string,
],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props

const emit = __emit
const wait = useWait()
const treatment20504 = checkoutModalSimplification20504Variant() === 'B'

const stripeElementsRef = ref<typeof StripeElements | null>(null)
const expressCheckoutRef = ref<typeof StripeElement | null>(null)
const paymentElementRef = ref<typeof StripeElement | null>(null)

const validation = ref(new ValidationState())

// True once card data has been changed
const changed = ref(false)

// True once Stripe reports the the card is valid (but we din't save it yet).
const complete = ref(false)
// True while we are saving card data.
const saving = ref(false)
// True once we successfully saved card, triggers the success alert.
const saved = ref(false)

// This value is used as the `key` to the `StripeElements` component,
// so we can toggle it to remount the component.
const remountStripeElements = ref(false)
const stripeTheme = computed<'light' | 'dark'>(() =>
  nightMode.active ? 'dark' : 'light',
)

// The onboarding payment step hides its own chrome until both Stripe
// elements have finished loading.
const expressCheckoutReady = ref(false)
const paymentElementReady = ref(false)
const expressCheckoutWalletsAvailable = ref(false)
// Stripe shows "See more" once a second wallet overflows the single row.
const expressCheckoutMultipleWallets = ref(false)

const allVisibleElementsReady = computed(
  () => expressCheckoutReady.value && paymentElementReady.value,
)

function onExpressCheckoutReady(event?: {
  availablePaymentMethods?: Record<string, boolean> | null
}): void {
  expressCheckoutReady.value = true
  if (!treatment20504) {
    return
  }
  const methods = event?.availablePaymentMethods
  const availableWalletCount = methods
    ? Object.values(methods).filter(Boolean).length
    : 0
  expressCheckoutWalletsAvailable.value = availableWalletCount > 0
  expressCheckoutMultipleWallets.value = availableWalletCount > 1
}

watch(allVisibleElementsReady, (ready) => {
  if (ready) {
    emit('elementsReady')
  }
})

const setupIntent = ref<Record<string, any>>({})
// True once we confirmed the setupIntent
const setupIntentConfirmed = ref(false)
// One-shot flag indicating that Stripe wasn't available at mount time,
// so we should remount elements when Stripe reports it finished loading.
// https://github.com/shortformhq/main/issues/14547
let isWaitingForStripe = ref(false)

// https://stripe.com/docs/js/initializing#init_stripe_js-options
const instanceOptions = ref<Record<string, any>>({})
// https://stripe.com/docs/stripe.js#element-options
const paymentElementOptions = ref<StripePaymentElementOptions>({
  layout: {
    type: 'tabs',
    defaultCollapsed: false,
  },
  ...(treatment20504 ? { wallets: { link: 'never' } } : {}),
})

// https://stripe.com/docs/js/elements_object/create#stripe_elements-options
const elementsOptions = computed<StripeElementsOptionsMode>(() => ({
  mode: 'setup',
  currency: 'usd',
  appearance: stripeAppearance(stripeTheme.value),
}))

onBeforeMount(async () => {
  checkStripeLoadedOnMount()
  emitter.on('stripeFinishedLoading', remountWhenStripeLoads)

  // Pre-fill user's email for the Stripe Link payment
  const userEmail = getEmail()
  if (userEmail) {
    paymentElementOptions.value.defaultValues = {
      billingDetails: {
        email: userEmail,
      },
    }
  } else {
    Sentry.captureMessage('User email is null in the subscribe popup')
  }
  setupIntent.value = await backend.getSetupIntent()
})

watch(
  // @ts-ignore
  () => expressCheckoutRef.value?.stripeElement,
  (element) => {
    if (!element) {
      return
    }

    const handleReady = (): void => {
      element.addEventListener('confirm', expressCheckoutConfirm)
    }

    // When Stripe Element `on()` function becomes available, attach handler
    if (typeof element.on === 'function') {
      element.on('ready', handleReady)
    } else {
      // Fallback: bind immediately if already initialized (past the `ready` state)
      handleReady()
    }
  },
  { immediate: true },
)

onBeforeUnmount(async () => {
  // If the user didn't add payment data, cancel the setup intent
  if (!setupIntentConfirmed.value) {
    await backend.cancelSetupIntent(setupIntent.value)
  }
  emitter.off('stripeFinishedLoading', remountWhenStripeLoads)
})

/**
 * Capture any uncaught errors in this component and log to Sentry.
 *
 * We are not sure if we can catch any errors here, this is a test approach to see
 * if we log any errors to Stripe from this handler.
 */
onErrorCaptured((error) => {
  Sentry.captureException(error)
  return false
})

function changedHandler($event: any): void {
  changed.value = true
  complete.value = $event.complete
}

/**
 * Check if Stripe was loaded when the component mounted.
 *
 * Sets the `isWaitingForStripe` flag accordingly.
 */
function checkStripeLoadedOnMount(): void {
  isWaitingForStripe.value = window.Stripe === undefined
}

function stripeElementLoadErrorHandler(event: any): void {
  const error =
    event?.error instanceof Error
      ? event.error
      : new Error(event?.error?.message || 'Stripe element load error')

  Sentry.withScope((scope) => {
    scope.setTag('service', 'stripe')
    scope.setExtra('stripeLoadErrorEvent', event)
    Sentry.captureException(error)
  })

  // `elementsReady` never fires after this (the failed element never
  // reports `ready`) -- let consumers know so they can offer a way
  // forward instead of leaving the user stuck with no working UI.
  emit('loadError')
}

function stripeAppearance(
  theme: 'light' | 'dark',
): NonNullable<StripeElementsOptionsMode['appearance']> {
  const palette =
    theme === 'dark'
      ? {
          inputBorder: '#505258',
          inputBackground: '#1D1E20',
          tabBorder: '#505258',
          tabBackground: '#1D1E20',
          tabSelectedBorder: '#3876F0',
          tabSelectedBackground: 'rgba(56, 118, 240, 0.28)',
        }
      : {
          inputBorder: '#D5D8E0',
          inputBackground: undefined,
          tabBorder: '#D4D0D0',
          tabBackground: undefined,
          tabSelectedBorder: '#363B48',
          tabSelectedBackground: undefined,
        }

  const rules = {
    '.Input': {
      border: `1px solid ${palette.inputBorder}`,
      lineHeight: '20px',
      ...(palette.inputBackground
        ? { backgroundColor: palette.inputBackground }
        : {}),
      ...(treatment20504
        ? {
            borderRadius: '8px',
            padding: '12px 16px',
            ...(theme === 'light' ? { backgroundColor: '#F8F8F8' } : {}),
          }
        : {}),
    },
    '.Label': {
      fontWeight: '500',
      lineHeight: '18px',
      ...(treatment20504
        ? {
            fontSize: '16px',
            color: theme === 'dark' ? '#EEEFF0' : '#141C31',
          }
        : {}),
    },
    '.Tab': {
      border: `1px solid ${palette.tabBorder}`,
      ...(palette.tabBackground
        ? { backgroundColor: palette.tabBackground }
        : {}),
      ...(treatment20504 ? { borderRadius: '8px' } : {}),
    },
    '.Tab--selected': {
      border: `1px solid ${palette.tabSelectedBorder}`,
      ...(palette.tabSelectedBackground
        ? { backgroundColor: palette.tabSelectedBackground }
        : {}),
      ...(treatment20504 ? { borderRadius: '8px' } : {}),
    },
    '.TermsText': {
      fontSize: treatment20504 ? '11px' : '12px',
      ...(treatment20504
        ? {
            lineHeight: '18px',
            color: theme === 'dark' ? '#A7AAB3' : '#6B6D76',
          }
        : {}),
    },
  }

  return {
    // To style the Payment Element we use Stripe Appearance API
    // https://docs.stripe.com/elements/appearance-api
    theme: theme === 'dark' ? 'night' : 'stripe',
    variables: {
      ...(theme === 'dark'
        ? {
            colorPrimaryText: '#EEEFF0',
            colorText: '#EEEFF0',
            colorTextSecondary: '#A7AAB3',
            colorBackground: '#1D1E20',
            fontFamily: 'Rotunda, sans-serif',
            fontSizeBase: '16px',
          }
        : {
            colorPrimaryText: '#141C31',
            colorBackground: '#F8F8F8',
            fontFamily: 'Rotunda, sans-serif',
            fontSizeBase: '16px',
          }),
      ...(treatment20504
        ? {
            borderRadius: '24px',
            ...(theme === 'light' ? { colorBackground: '#FFFFFF' } : {}),
          }
        : {}),
    },
    rules: rules as NonNullable<
      NonNullable<StripeElementsOptionsMode['appearance']>['rules']
    >,
  }
}

function expressCheckoutOptionsForTheme(
  theme: 'light' | 'dark',
): StripeExpressCheckoutElementOptions {
  const blackButtons = treatment20504 || theme === 'dark'
  return {
    ...(treatment20504
      ? {
          buttonHeight: 48,
          layout: {
            maxColumns: 3,
            maxRows: 1,
          },
        }
      : {}),
    buttonType: {
      applePay: 'subscribe',
      googlePay: 'subscribe',
    },
    buttonTheme: {
      applePay: blackButtons ? 'black' : 'white-outline',
      googlePay: blackButtons ? 'black' : 'white',
    },
    paymentMethods: {
      applePay: 'always',
      googlePay: 'always',
    },
    paymentMethodOrder: ['apple_pay', 'google_pay', 'link'],
  }
}

function needsSaving(): boolean {
  return changed.value
}

async function paymentElementConfirmStripe(): Promise<any> {
  // @ts-ignore
  const confirmSetupFn = stripeElementsRef.value.instance.confirmSetup
  const params = {
    // @ts-ignore
    elements: stripeElementsRef.value.elements,
    clientSecret: setupIntent.value.client_secret,
    redirect: 'if_required',
    confirmParams: {
      return_url: 'https://www.shortform.com/app/billing',
    },
  }
  // @ts-ignore
  await stripeElementsRef.value.elements.submit()
  const { error } = await confirmSetupFn(params)
  if (error) {
    return { error: error }
  }

  setupIntentConfirmed.value = true
  return { success: true }
}

/**
 * Express Checkout Element click handler.
 *
 * We need to have the click handler for the element,
 * because we need to immediately call `event.resolve()` with `client_secret`.
 */
async function expressCheckoutClick(event: Event): Promise<any> {
  // @ts-ignore
  event.resolve(setupIntent.value.clientSecret)
}

/**
 * Express Checkout Element confirm handler.
 *
 * This method is called after the user goes through the payment option's
 * process: for example, chooses card, clicks confirm.
 */
async function expressCheckoutConfirm(): Promise<boolean> {
  if (saving.value) {
    return false
  }
  saving.value = true
  let loading = false

  try {
    const { error: submitError } =
      // @ts-ignore
      await stripeElementsRef.value.elements.submit()
    if (submitError) {
      handleStripeError(submitError)
      return false
    }

    // Create a ConfirmationToken using the details collected by the Express Checkout Element
    // @ts-ignore
    const { error, confirmationToken } =
      // @ts-ignore
      await stripeElementsRef.value.instance.createConfirmationToken({
        // @ts-ignore
        elements: stripeElementsRef.value.elements,
        params: {
          return_url: 'https://www.shortform.com/app/billing',
        },
      })

    if (error) {
      // From Stripe documentation:
      // This point is only reached if there's an immediate error when
      // confirming the payment. Show the error to your customer (for example, payment details incomplete)
      // https://docs.stripe.com/elements/express-checkout-element/accept-a-payment?client=html#create-ct
      handleStripeError(error)
      return false
    }

    // @ts-ignore
    const confirmSetupFn = stripeElementsRef.value.instance.confirmSetup
    const params = {
      clientSecret: setupIntent.value.client_secret,
      redirect: 'if_required',
      confirmParams: {
        confirmation_token: confirmationToken.id,
        return_url: 'https://www.shortform.com/app/billing',
      },
    }
    // @ts-ignore
    const { error: confirmationError, setupIntent: confirmedIntent } =
      await confirmSetupFn(params)
    if (confirmationError) {
      handleStripeError(confirmationError)
      return false
    }
    if (
      confirmedIntent?.status !== 'succeeded' ||
      confirmedIntent.id !== setupIntent.value.id
    ) {
      handleStripeError({
        message: 'Unable to confirm your payment method. Please try again.',
      })
      return false
    }
    setupIntentConfirmed.value = true
    wait.start('Loading')
    loading = true
    const billing = await backend.savePaymentMethod({
      id: setupIntent.value.id,
    })
    props.billing.card_data = billing.card_data
    _setSaved('')

    // There is a new billing card data returned by stripe,
    // if we saved it successfully, update props.billing,
    // so the new card is displayed.
    emit('saved')

    return true
  } catch (error) {
    if (loading) {
      _setSaved(error)
    } else {
      handleStripeError(error)
    }
    return false
  } finally {
    saving.value = false
    if (loading) {
      wait.end('Loading')
    }
  }
}

/**
 * Save card, returns true on success
 */
async function save(): Promise<boolean> {
  saving.value = true

  try {
    const data = await paymentElementConfirmStripe()
    if (data.error) {
      handleStripeError(data.error)
      return false
    }
    const billing = await backend.savePaymentMethod({
      id: setupIntent.value.id,
    })
    props.billing.card_data = billing.card_data
    _setSaved('')

    // There is a new billing card data returned by stripe,
    // if we saved it successfully, update props.billing,
    // so the new card is displayed.
    emit('saved')
    return true
  } catch (error) {
    _setSaved(error)
  }
  return false
}

/**
 * Update state flags depending on the error value.
 */
function _setSaved(error: any): void {
  if (error) {
    saving.value = false
    saved.value = false
    changed.value = true
    validation.value.showErrors(error)
  } else {
    saving.value = false
    saved.value = true
    changed.value = false
    validation.value.reset()
  }
}

function handleStripeError(error: any): void {
  // Something went wrong on Stripe side.
  const expected_errors = [
    'incomplete_zip',
    'incomplete_cvc',
    'incomplete_expiry',
    'incomplete_number',
    'invalid_number',
    'invalid_expiry_month_past',
  ]
  if (error.code && expected_errors.indexOf(error.code) === -1) {
    // Unexpected error, send to Sentry.
    Sentry.withScope((scope) => {
      scope.setExtra('stripeData', error)
      Sentry.captureMessage(error.message)
    })
  }
  _setSaved({
    response: {
      status: 400,
      data: { errors: { card: [error.message] } },
    },
  })
}

const savedAlert = computed(() => {
  if (saved.value) {
    // 3 seconds to hide the alert
    return 3
  }
  return false
})

/**
 * Remount Stripe Elements when Stripe finishes loading, if it was missing on mount.
 *
 * If Stripe was missing on mount, we set the `isWaitingForStripe` flag to true.
 * Once we receive the `stripeFinishedLoading` event, we check the flag and remount
 * the Stripe Elements if needed.
 */
function remountWhenStripeLoads(): void {
  // Stripe was loaded on component mount, no need to remount the Stripe Elements
  if (!isWaitingForStripe.value) {
    return
  }

  if (!window.Stripe) {
    // Stripe is still not available, log an error to Sentry.
    // We received the `stripeFinishedLoading` event, so this would be unexpected.
    Sentry.captureException(
      new Error(
        'StripeCard component: Stripe instance undefined despite receiving stripeFinishedLoading event',
      ),
    )
    isWaitingForStripe.value = false
    return
  }

  isWaitingForStripe.value = false
  if (!stripeElementsRef.value?.elements) {
    // Value is irrelevant here, we just need to change it to trigger a remount
    // of the StripeElements component
    remountStripeElements.value = !remountStripeElements.value
    expressCheckoutReady.value = false
    expressCheckoutWalletsAvailable.value = false
    expressCheckoutMultipleWallets.value = false
    paymentElementReady.value = false
  }
}

__expose({
  needsSaving,
  save,
})

return (_ctx: any,_cache: any) => {
  const _component_loading_spinner = _resolveComponent("loading-spinner")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      class: _normalizeClass(["stripe-payment-element", { 'stripe-payment-element--20504': treatment20504 }])
    }, [
      (_unref(wait).is('Loading'))
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_loading_spinner)
          ]))
        : _createCommentVNode("", true),
      (_openBlock(), _createBlock(_unref(StripeElements), {
        ref_key: "stripeElementsRef",
        ref: stripeElementsRef,
        key: `${remountStripeElements.value}`,
        "stripe-key": _unref(STRIPE_PUBLIC_KEY),
        "instance-options": instanceOptions.value,
        "elements-options": elementsOptions.value,
        class: "stripe-elements"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", {
            class: _normalizeClass({
          'stripe-payment-element__express-framed-20504':
            expressCheckoutWalletsAvailable.value,
          'stripe-payment-element__express-framed-20504--multiple':
            expressCheckoutMultipleWallets.value,
        })
          }, [
            (expressCheckoutWalletsAvailable.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Express checkout "))
              : _createCommentVNode("", true),
            (_openBlock(), _createBlock(_unref(StripeElement), {
              id: "express-checkout-element",
              key: `${stripeTheme.value}`,
              ref_key: "expressCheckoutRef",
              ref: expressCheckoutRef,
              type: 'expressCheckout',
              options: expressCheckoutOptionsForTheme(stripeTheme.value),
              onReady: onExpressCheckoutReady,
              onChange: changedHandler,
              onClick: expressCheckoutClick,
              onLoaderror: stripeElementLoadErrorHandler
            }, null, 8, ["options"]))
          ], 2),
          (expressCheckoutWalletsAvailable.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
                _createElementVNode("span", null, "or pay with card", -1)
              ]))]))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(_unref(StripeElement), {
              id: "payment-element",
              ref_key: "paymentElementRef",
              ref: paymentElementRef,
              type: 'payment',
              options: paymentElementOptions.value,
              onReady: _cache[0] || (_cache[0] = ($event: any) => (paymentElementReady.value = true)),
              onChange: changedHandler,
              onLoaderror: stripeElementLoadErrorHandler
            }, null, 8, ["options"]),
            _renderSlot(_ctx.$slots, "actions")
          ])
        ]),
        _: 3
      }, 8, ["stripe-key", "instance-options", "elements-options"]))
    ], 2),
    _createVNode(FormError, {
      class: _normalizeClass(validation.value.stateClass('card') === 'is-invalid' ? 'd-block' : ''),
      errors: validation.value.errors,
      field: "card"
    }, null, 8, ["class", "errors"]),
    (__props.showButton)
      ? (_openBlock(), _createElementBlock("button", {
          key: 0,
          class: "btn btn-primary mt-3",
          disabled: !complete.value || saving.value,
          onClick: save
        }, " Save Card ", 8, _hoisted_5))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("div", _hoisted_6, "Card Saved", 512), [
      [_vShow, savedAlert.value]
    ])
  ], 64))
}
}

})